import { useContext, useMemo, useState } from 'react'
import { Select } from 'radix-ui'
import { cn } from '@/utlis/cn'
import SelectedCheckmarkIcon from '@/CustomIcons/SelectedCheckmarkIcon'
import { Button } from '@/UIComponents2/Button'
import { PortalContext } from '@/UIComponents2/PortalContext'
import LeftChevronIcon from '@/CustomIcons/LeftChevron'

type ChannelGroup = number[]
type ChannelGroupOption = {
    value: ChannelGroup
    strValue: string
    label: string
    disabled: boolean
    inUse: boolean
    tooltip?: string
}
type ChannelSelectProps = {
    value: ChannelGroup
    // The total number of channels on the device
    numChannels: number
    // The highest channel that can be selected (limited by some quality settings)
    maxChannel: number
    // Will render stereo pairs as 1+2 instead of 1-2 to indicate they're merged
    mergedChannels?: boolean
    // Indicates which channel groups are already shared and need to be disabled
    sharedGroups: ChannelGroup[]
    onChange?: (value: ChannelGroupOption | undefined) => void
    onRemove?: () => void
    disabled?: boolean
    label?: string
    className?: string
    triggerClassName?: string
    defaultOpen?: boolean
    onClose?: () => void
}
export function ChannelSelect(props: ChannelSelectProps) {
    const portalContainer = useContext(PortalContext)
    const [isOpen, setIsOpen] = useState(props.defaultOpen ?? false)

    const options = useMemo(() => {
        let opts: ChannelGroupOption[] = []
        for (let i = 0; i < props.numChannels; i++) {
            const inUse = !!props.sharedGroups.find((g) => g.length === 1 && g[0] === i)
            const blocked =
                !!props.sharedGroups.find((g) => g.length === 2 && (g[0] === i || g[1] === i)) &&
                !props.value.includes(i)
            const selected = props.value.join('-') === i.toString()
            const value = [i]
            opts.push({
                value,
                strValue: value.join('-'),
                label: (i + 1).toFixed(0),
                disabled: ((inUse || blocked) && !selected) || i > props.maxChannel,
                inUse,
            })
            if (i % 2 === 1) {
                const inUse = !!props.sharedGroups.find((g) => g[0] === i - 1 && g[1] === i)
                const value = [i - 1, i]
                const selected = props.value.join('-') === value.toString()
                const sep = props.mergedChannels ? '+' : '-'
                opts.push({
                    value,
                    strValue: value.join('-'),
                    label: `${i}${sep}${i + 1}`,
                    disabled: (inUse && !selected) || i > props.maxChannel,
                    inUse,
                })
            }
        }
        return opts
    }, [props.numChannels, props.sharedGroups, props.value, props.maxChannel, props.mergedChannels])

    const renderOption = (opt: ChannelGroupOption) => {
        return (
            <Select.Item
                key={opt.label}
                value={opt.strValue}
                textValue={opt.label}
                disabled={opt.disabled}
                className={cn(
                    'flex justify-between items-center cursor-pointer hover:bg-white/10 px-[16px] py-[8px]',
                    opt.inUse && 'text-muse-yellow',
                    opt.disabled && 'opacity-50 cursor-not-allowed'
                )}
            >
                <Select.ItemText>
                    <div className="min-w-[23px]">{opt.label}</div>
                </Select.ItemText>
                <Select.ItemIndicator className="text-muse-yellow">
                    <SelectedCheckmarkIcon />
                </Select.ItemIndicator>
            </Select.Item>
        )
    }

    const baseLayoutStyles = 'flex w-full py-[7px] gap-2 pl-4 pr-2 items-center justify-between'
    const baseBorderStyles = 'rounded-[12px] border'
    return (
        <Select.Root
            value={props.value.join('-')}
            onValueChange={(val) => props.onChange?.(options.find((op) => op.strValue === val))}
            defaultOpen={props.defaultOpen}
            onOpenChange={(open) => {
                if (!open) {
                    props.onClose?.()
                }
                setIsOpen(open)
            }}
        >
            <div className={cn('min-w-0 flex flex-col gap-2', props.className)}>
                {props.label && <div>{props.label}</div>}
                <Select.Trigger
                    className={cn(
                        baseLayoutStyles,
                        baseBorderStyles,
                        'w-full',
                        'border-muse-toplit',
                        'shadow-glow',
                        'bg-[rgba(255,255,255,0.03)] cursor-pointer',
                        props.disabled && 'opacity-50 cursor-not-allowed',
                        props.triggerClassName
                    )}
                >
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="truncate whitespace-nowrap">
                            <Select.Value className="truncate pt-[2px] min-w-0" />
                        </div>
                    </div>

                    <Select.Icon className={cn('transition-transform', isOpen ? 'rotate-90' : '-rotate-90')}>
                        <LeftChevronIcon
                            className="opacity-65"
                            width="20px"
                            height="20px"
                            style={{ fontSize: '1.3rem' }}
                        />
                    </Select.Icon>
                </Select.Trigger>
            </div>
            <Select.Portal container={portalContainer}>
                <Select.Content
                    position="popper"
                    sideOffset={5}
                    className={cn(
                        baseBorderStyles,
                        'z-30 border-white/15',
                        'shadow-[inset_2px_4px_16px_0px_rgba(255,255,255,0.1)]',
                        'bg-muse-menu py-[20px] bg-blend-luminosity'
                    )}
                >
                    <div className="max-h-[40vh] overflow-y-auto">
                        {props.sharedGroups.length > 1 && props.value.length > 0 && (
                            <div className="px-[16px] pb-[8px]">
                                <Button
                                    variant="secondary"
                                    className="w-full py-1 text-sm"
                                    onClick={() => props.onRemove?.()}
                                >
                                    Remove {props.value.map((g) => g + 1).join('-')}
                                </Button>
                            </div>
                        )}
                        <div className="grid grid-cols-2">
                            <Select.Group className="flex flex-col border-r-1 border-r-white/5">
                                <Select.Label className="opacity-65 px-[16px] py-[8px]">Mono</Select.Label>
                                {options.filter((g) => g.value.length === 1).map((opt) => renderOption(opt))}
                            </Select.Group>
                            <Select.Group className="flex flex-col">
                                <Select.Label className="opacity-65 px-[16px] py-[8px]">Stereo</Select.Label>
                                {options.filter((g) => g.value.length === 2).map((opt) => renderOption(opt))}
                            </Select.Group>
                        </div>
                    </div>
                </Select.Content>
            </Select.Portal>
        </Select.Root>
    )
}
